import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton, NeuCard } from "../components/neu/Neu";
import { useCachedResource } from "../hooks/useCachedResource";
import { myBilling, startCheckout } from "../api/billing";
import { InvoiceList } from "../features/billing/InvoiceList";

const daysLeft = (iso) =>
  iso ? Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000)) : null;

export default function BillingPage() {
  const { t } = useTranslation();
  const { data } = useCachedResource("billing", () => myBilling());
  const [busy, setBusy] = useState("");

  const go = async (interval) => {
    setBusy(interval);
    try {
      const res = await startCheckout(interval);
      window.location.href = res.checkout_url;
    } catch {
      toast.error(t("errors.generic"));
      setBusy("");
    }
  };

  const c = data?.company;
  const left = daysLeft(c?.trial_ends_at);

  return (
    <AppShell title={t("billing.title")} subtitle={t("billing.note")}>
      {!data ? (
        <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
      ) : (
        <div className="grid gap-6" data-testid="billing-page">
          <div className="grid gap-6 lg:grid-cols-3">
          <NeuCard data-testid="billing-status">
            <p className="label-xs">{t("billing.status")}</p>
            <p className="mt-3 text-2xl font-black text-primary">
              {t(`billing.${c.subscription_status || "trialing"}`)}
            </p>
            <dl className="mt-4 space-y-2 text-sm">
              <Row label={t("billing.plan")} value={c.plan} />
              <Row label={t("billing.employees")} value={c.employee_count} />
              {c.trial_ends_at ? (
                <Row label={t("billing.trialEnds")} value={`${c.trial_ends_at.slice(0, 10)} · ${t("billing.daysLeft", { count: left })}`} />
              ) : null}
            </dl>
          </NeuCard>

          {[["monthly", data.monthly, "month"], ["annual", data.annual, "year"]].map(([key, quote, interval]) => (
            <NeuCard key={key} data-testid={`billing-plan-${interval}`}>
              <p className="label-xs">
                {key === "annual" ? t("billing.annual", { percent: quote.annual_discount_percent }) : t("billing.monthly")}
              </p>
              <p className="mt-3 font-mono text-3xl font-black">
                {quote.total} <span className="text-base">{quote.currency.toUpperCase()}</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {quote.price_per_employee} {quote.currency.toUpperCase()} {t("billing.perEmployee")}
              </p>
              <NeuButton
                variant="primary"
                className="mt-5 flex w-full items-center justify-center gap-2 py-4"
                data-testid={`billing-subscribe-${interval}`}
                disabled={busy === interval}
                onClick={() => go(interval)}
              >
                <CreditCard className="h-4 w-4" />
                {c.subscription_status === "active" ? t("billing.manage") : t("billing.subscribe")}
              </NeuButton>
            </NeuCard>
          ))}
          </div>
          <InvoiceList />
        </div>
      )}
    </AppShell>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-semibold">{value}</dd>
  </div>
);
